import {
  AcAppHeader,
  AcCard,
  AcTypography,
  AcAccordion,
  AcAccordionItem,
  AcSelect,
  AcSwitch,
  AcLoader, AcTooltip
} from '@components'
import {LABELS, ROUTE_PATHS} from '@constants'
import {observer} from 'mobx-react-lite'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useStore} from '@hooks/use-store'
import dayjs from 'dayjs'

const REFRESH_OPTIONS = [
  {id: '15000', label: '15s'},
  {id: '30000', label: '30s'},
  {id: '60000', label: '1m'},
  {id: '300000', label: '5m'}
]

export const AcSystemJobBatchDetail = observer(() => {
  const {id} = useParams()
  const navigate = useNavigate()
  const {job_batches} = useStore()

  const [intervalMs, setIntervalMs] = useState<number>(15000)
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false)
  const initializedAutoRef = useRef<boolean>(false)
  const intervalRef = useRef<number | null>(null)

  useEffect(() => {
    if (id) job_batches.getJobBatch(id)
    return () => {
      job_batches.job_batch = null
    }
  }, [id])

  const data = job_batches.watch_job_batch
  const batch = useMemo(() => data?.batch ?? data ?? {}, [data])
  const counts = useMemo(() => {
    const total = batch?.counts?.total ?? 0
    const processed = batch?.counts?.processed ?? 0
    const failed = batch?.counts?.failed ?? 0
    const pending = batch?.counts?.pending ?? 0
    const createdAt = batch?.created_at ?? batch?.createdAt
    return {total, processed, failed, pending, createdAt}
  }, [batch])

  const finished = (batch.finished_at != null) || (counts.total > 0 && counts.pending === 0)
  const progressPct = counts.total > 0 ? Math.round(((counts.processed + counts.failed) / counts.total) * 100) : 0

  // Enable auto-refresh by default when first data arrives and batch not finished
  useEffect(() => {
    if (!initializedAutoRef.current && data) {
      if (!finished) setAutoRefresh(true)
      initializedAutoRef.current = true
    }
  }, [data, finished])

  useEffect(() => {
    if (autoRefresh) {
      if (intervalRef.current) window.clearInterval(intervalRef.current)
      intervalRef.current = window.setInterval(() => {
        if (id) job_batches.getJobBatch(id)
      }, intervalMs)
    }
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [autoRefresh, intervalMs, id])

  // Stop auto refresh when finished
  useEffect(() => {
    if (finished && intervalRef.current) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
      setAutoRefresh(false)
    }
  }, [finished])

  const outstandingJobs = useMemo(() => {
    const jobs = data?.jobs ?? []
    if (!Array.isArray(jobs)) return []
    return jobs.filter((j: any) => {
      const status = j?.status ?? j?.state ?? j?.finished_at ? 'done' : 'pending'
      return status !== 'done'
    }).slice(0, 10)
  }, [data])

  return (
    <>
      <AcAppHeader title={`Batch: ${batch?.name || id}`} showBackButton action={
        <div style={{display: 'flex', alignItems: 'center', gap: 12, marginTop: 12}}>
          <AcTypography element="span" size="sm">Auto-refresh</AcTypography>
          <AcSwitch checked={autoRefresh} onClick={() => setAutoRefresh(v => !v)}/>
          <AcSelect
            name="refresh-interval"
            id="batch-detail-refresh-interval"
            options={REFRESH_OPTIONS}
            valueKey="id"
            labelKey="label"
            defaultValue={REFRESH_OPTIONS.find(o => o.id === String(intervalMs))}
            onChange={(opt: any) => setIntervalMs(parseInt(opt?.id ?? opt))}
            placeholder="Interval"
            noMinWidth
          />
        </div>
      }/>
      <div style={{display: 'grid', gridTemplateRows: '1fr 1fr', gap: 16}}>
        <div>
          {job_batches.is_loading && !data ? (
            <AcLoader/>
          ) : data ? (
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16}}>
              <AcCard>
                <div>
                  <div style={{marginBottom: 5}}>
                    <AcTypography element="span" weight="semibold">ID: </AcTypography>
                    <AcTypography element="span" size="sm">{batch?.id}</AcTypography>
                  </div>
                  <div style={{marginBottom: 5}}>
                    <AcTypography element="span" weight="semibold">Name: </AcTypography>
                    <AcTypography element="span" size="sm">{batch?.name}</AcTypography>
                  </div>
                  <div style={{marginBottom: 5}}>
                    <AcTypography element="span" weight="semibold">Created at: </AcTypography>
                      <AcTooltip
                        label={dayjs(batch?.created_at).format('DD-MM-YYYY HH:mm')}>
                        <AcTypography
                          size="sm"
                          weight="light"
                          element="span">
                          {dayjs(batch?.created_at).format('DD-MM-YYYY')}
                        </AcTypography>
                      </AcTooltip>
                  </div>
                  <div style={{marginBottom: 5}}>
                    <AcTypography element="span" weight="semibold">Finished at: </AcTypography>
                    {batch?.finished_at ?
                        <AcTooltip
                          label={dayjs(batch?.finished_at).format('DD-MM-YYYY HH:mm')}>
                          <AcTypography
                            size="sm"
                            weight="light"
                            element="span">
                            {dayjs(batch?.finished_at).format('DD-MM-YYYY')}
                          </AcTypography>
                        </AcTooltip>
                      : '-'}
                  </div>
                  <div style={{marginBottom: 5}}>
                    <AcTypography element="span" weight="semibold">Cancelled at: </AcTypography>
                    {batch?.cancelled_at ?
                        <AcTooltip
                          label={dayjs(batch?.cancelled_at).format('DD-MM-YYYY HH:mm')}>
                          <AcTypography
                            size="sm"
                            weight="light"
                            element="span">
                            {dayjs(batch?.cancelled_at).format('DD-MM-YYYY')}
                          </AcTypography>
                        </AcTooltip>
                      : '-'}
                  </div>
                </div>
              </AcCard>
              <AcCard>
                <AcTypography weight="semibold">Batch progress</AcTypography>
                <div style={{display: 'flex', alignItems: 'center', gap: 12, marginTop: 8}}>
                  <div style={{flex: 1, height: 10, background: '#eee', borderRadius: 6, overflow: 'hidden'}}>
                    <div style={{
                      width: `${progressPct}%`,
                      height: '100%',
                      background: progressPct === 100 ? '#16a34a' : '#3b82f6'
                    }}/>
                  </div>
                  <AcTypography element="span" size="sm">{progressPct}%</AcTypography>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 8}}>
                  <div><AcTypography weight="semibold">Total</AcTypography>
                    <div><AcTypography element="span" size="sm">{counts.total}</AcTypography></div>
                  </div>
                  <div><AcTypography weight="semibold">Processed</AcTypography>
                    <div><AcTypography element="span" size="sm">{counts.processed}</AcTypography></div>
                  </div>
                  <div><AcTypography weight="semibold">Pending</AcTypography>
                    <div><AcTypography element="span" size="sm">{counts.pending}</AcTypography></div>
                  </div>
                  <div><AcTypography weight="semibold">Failed</AcTypography>
                    <div><AcTypography element="span" size="sm">{counts.failed}</AcTypography></div>
                  </div>
                </div>
              </AcCard>
            </div>
          ) : (
            <AcTypography>-</AcTypography>
          )}
        </div>

        <AcCard>
        {job_batches.is_loading && !data ? (
          <AcLoader/>
        ) : data ? (
          <>
            <AcTypography weight="semibold">Jobs</AcTypography>
            {(() => {
              const jobs = (data?.jobs ?? batch?.jobs ?? []) as any[]
              if (!Array.isArray(jobs) || jobs.length === 0) {
                return <AcTypography size="sm">-</AcTypography>
              }
              return (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px 6px' }}>ID</th>
                        <th style={{ textAlign: 'left', padding: '8px 6px' }}>Queue</th>
                        <th style={{ textAlign: 'left', padding: '8px 6px' }}>Payload</th>
                        <th style={{ textAlign: 'left', padding: '8px 6px' }}>Attempts</th>
                        <th style={{ textAlign: 'left', padding: '8px 6px' }}>Reserved At</th>
                        <th style={{ textAlign: 'left', padding: '8px 6px' }}>Available At</th>
                        <th style={{ textAlign: 'left', padding: '8px 6px' }}>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((j: any) => (
                        <tr key={String(j.id ?? j.uuid ?? Math.random())}>
                          <td style={{ padding: '6px' }}>
                            <AcTypography size="sm" element="span" weight="light">{String(j.id ?? '-') }</AcTypography>
                          </td>
                          <td style={{ padding: '6px' }}>
                            <AcTypography size="sm" element="span" weight="light">{String(j.queue ?? '-')}</AcTypography>
                          </td>
                          <td style={{ padding: '6px' }}>
                            <AcTypography size="sm" element="span" weight="light">{String(j.payload ?? '-')}</AcTypography>
                          </td>
                          <td style={{ padding: '6px' }}>
                            <AcTypography size="sm" element="span" weight="light">{String(j.attempts ?? '-')}</AcTypography>
                          </td>
                          <td style={{ padding: '6px' }}>
                            {j?.reserved_at ? (
                              <AcTooltip label={dayjs(j.reserved_at).format('DD-MM-YYYY HH:mm')}>
                                <AcTypography size="sm" weight="light" element="span">
                                  {dayjs(j.reserved_at).format('DD-MM-YYYY')}
                                </AcTypography>
                              </AcTooltip>
                            ) : (
                              <AcTypography size="sm" weight="light" element="span">-</AcTypography>
                            )}
                          </td>
                          <td style={{ padding: '6px' }}>
                            {j?.available_at ? (
                              <AcTooltip label={dayjs(j.available_at).format('DD-MM-YYYY HH:mm')}>
                                <AcTypography size="sm" weight="light" element="span">
                                  {dayjs(j.available_at).format('DD-MM-YYYY')}
                                </AcTypography>
                              </AcTooltip>
                            ) : (
                              <AcTypography size="sm" weight="light" element="span">-</AcTypography>
                            )}
                          </td>
                          <td style={{ padding: '6px' }}>
                            {j?.created_at ? (
                              <AcTooltip label={dayjs(j.created_at).format('DD-MM-YYYY HH:mm')}>
                                <AcTypography size="sm" weight="light" element="span">
                                  {dayjs(j.created_at).format('DD-MM-YYYY')}
                                </AcTypography>
                              </AcTooltip>
                            ) : (
                              <AcTypography size="sm" weight="light" element="span">-</AcTypography>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            })()}
          </>
        ) : (
          <AcTypography>-</AcTypography>
        )}
      </AcCard>
      </div>
    </>
  )
})
